import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import '../App.css';
import BuildingSearchField from './BuildingSearchField';
import {
  fetchBuildings,
  selectBuildingsList,
  selectBuildingsListStatus,
  BuildingSummary,
} from '../features/buildingEditor/buildingEditor';

const columns: GridColDef<BuildingSummary>[] = [
  { field: 'address', headerName: 'Address', flex: 2 },
  { field: 'uniqueCode', headerName: 'Unique Code', flex: 1 },
  { field: 'buildingName', headerName: 'Name', flex: 1 },
  { field: 'status', headerName: 'Status', flex: 1 },
  {
    field: 'legacyType',
    headerName: 'Type',
    flex: 1,
    valueGetter: (params) => params.row.legacyType || params.row.type,
  },
  {
    field: 'parentIds',
    headerName: 'Parents',
    width: 90,
    sortable: false,
    renderCell: (params) => (
      params.row.parentIds.length > 0
        ? <Chip size="small" label={params.row.parentIds.length} />
        : null
    ),
  },
];

const BuildingEditor = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const list = useAppSelector(selectBuildingsList);
  const listStatus = useAppSelector(selectBuildingsListStatus);

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 400);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  useEffect(() => {
    dispatch(fetchBuildings({
      search: debouncedSearch,
      limit: paginationModel.pageSize,
      skip: paginationModel.page * paginationModel.pageSize,
    }));
  }, [debouncedSearch, paginationModel]);

  // reset to first page when the search changes
  useEffect(() => {
    setPaginationModel((model) => ({ ...model, page: 0 }));
  }, [debouncedSearch]);

  const handleRowClick = (params: GridRowParams<BuildingSummary>) => {
    navigate(`/dashboard/building-editor/${params.row.id}`);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Toolbar />
      <Typography variant="h5" sx={{ mb: 1 }}>
        Building Editor
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Edits here go straight to the building database that live service
        qualification reads: a change to a building&apos;s status or address is
        what the signup form sells on, immediately.
      </Alert>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <BuildingSearchField
              label="Search buildings — pick a match to open it, or keep typing to filter the list"
              onPick={(b) => navigate(`/dashboard/building-editor/${b.id}`)}
              onInputChange={setSearch}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => navigate('/dashboard/building-editor/new')}
          >
            New Building
          </Button>
        </Box>
        <DataGrid
          autoHeight
          rows={list.items}
          columns={columns}
          rowCount={list.total}
          loading={listStatus === 'loading'}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Paper>
    </Box>
  );
};

export default BuildingEditor;
